import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Stack, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, IconButton, CircularProgress, Chip, Avatar,
    Tooltip, Divider, Alert, InputAdornment, Checkbox,
    List, ListItem, ListItemText, ListItemAvatar
} from '@mui/material';
import {
    Groups as GroupsIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
    Search as SearchIcon, Person as PersonIcon,
    PersonAdd as PersonAddIcon, PersonRemove as PersonRemoveIcon,
    EventNote as EventNoteIcon,
    Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';

const CATEGORIES = ['ACADEMIC', 'CLUB', 'HOUSE', 'SPORT', 'OTHER'];
const CATEGORY_COLORS: Record<string, { color: string; bg: string }> = {
    ACADEMIC: { color: '#4C51BF', bg: '#EBF4FF' },
    CLUB:     { color: '#38A169', bg: '#F0FFF4' },
    HOUSE:    { color: '#DD6B20', bg: '#FFFAF0' },
    SPORT:    { color: '#E53E3E', bg: '#FFF5F5' },
    OTHER:    { color: '#718096', bg: '#F7FAFC' },
};

const GroupsPage = () => {
    const navigate = useNavigate();
    const [groups, setGroups] = useState<any[]>([]);
    const [teachers, setTeachers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategoryFilter, setActiveCategoryFilter] = useState('ALL');
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [membersDialogOpen, setMembersDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<any>(null);

    // Create/Edit form
    const [formData, setFormData] = useState({
        name: '', grade: '', classSuffix: '', teacherId: '', category: 'ACADEMIC'
    });

    // Student search within Members dialog
    const [studentSearchQuery, setStudentSearchQuery] = useState('');
    const [searchedStudents, setSearchedStudents] = useState<any[]>([]);
    const [searchingStudents, setSearchingStudents] = useState(false);
    const [currentMembers, setCurrentMembers] = useState<any[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

    const userInfo = localStorage.getItem('userInfo');
    const user = userInfo ? JSON.parse(userInfo).user || JSON.parse(userInfo) : null;
    const isAdmin = ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL'].includes(user?.role);

    const getHeaders = useCallback(() => {
        const token = userInfo ? JSON.parse(userInfo).token : null;
        return { Authorization: `Bearer ${token}` };
    }, [userInfo]);

    // ─── Fetch Groups ───────────────────────────────────────────────────
    const fetchGroups = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(getApiUrl('groups/my'), { headers: getHeaders() });
            setGroups(res.data || []);
        } catch (err) {
            console.error('Fetch groups error:', err);
        } finally {
            setLoading(false);
        }
    }, [getHeaders]);

    const fetchTeachers = useCallback(async () => {
        try {
            const res = await axios.get(getApiUrl('users'), { headers: getHeaders() });
            setTeachers((res.data || []).filter((u: any) => u.role === 'TEACHER'));
        } catch (err) {
            console.error('Fetch teachers error:', err);
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchGroups();
        if (isAdmin) fetchTeachers();
    }, [fetchGroups, fetchTeachers, isAdmin]);

    // ─── Create Group ───────────────────────────────────────────────────
    const handleCreateGroup = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await axios.post(getApiUrl('groups'), formData, { headers: getHeaders() });
            setCreateDialogOpen(false);
            setFormData({ name: '', grade: '', classSuffix: '', teacherId: '', category: 'ACADEMIC' });
            setSuccessMsg('Class group created successfully!');
            fetchGroups();
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error creating group');
        }
    };

    // ─── Edit Group ─────────────────────────────────────────────────────
    const openEditDialog = (group: any) => {
        setSelectedGroup(group);
        setFormData({
            name: group.name, grade: group.grade, classSuffix: group.classSuffix || '',
            teacherId: group.teacherId || '', category: group.category || 'ACADEMIC'
        });
        setEditDialogOpen(true);
    };

    const handleUpdateGroup = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await axios.put(getApiUrl(`groups/${selectedGroup.id}`), formData, { headers: getHeaders() });
            setEditDialogOpen(false);
            setSuccessMsg('Group updated successfully!');
            fetchGroups();
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error updating group');
        }
    };

    // ─── Delete Group ───────────────────────────────────────────────────
    const handleDeleteGroup = async (groupId: string, groupName: string) => {
        if (!window.confirm(`Delete "${groupName}"? This will remove all student memberships.`)) return;
        try {
            await axios.delete(getApiUrl(`groups/${groupId}`), { headers: getHeaders() });
            setSuccessMsg('Group deleted');
            fetchGroups();
            setTimeout(() => setSuccessMsg(''), 2000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error deleting group');
        }
    };

    // ─── Members Dialog ─────────────────────────────────────────────────
    const openMembersDialog = async (group: any) => {
        setSelectedGroup(group);
        setMembersDialogOpen(true);
        setStudentSearchQuery('');
        setSearchedStudents([]);
        setSelectedStudentIds([]);
        setLoadingMembers(true);
        try {
            const res = await axios.get(getApiUrl(`groups/${group.id}`), { headers: getHeaders() });
            setCurrentMembers(res.data?.students || []);
        } catch (err) {
            console.error('Error fetching members:', err);
        } finally {
            setLoadingMembers(false);
        }
    };

    // ─── Student Search within group management ─────────────────────────
    // Searches by grade AND/OR class (e.g., "Grade 7" or "Green" or "7 Green")
    const searchStudents = async (query: string) => {
        if (query.trim().length < 2) {
            setSearchedStudents([]);
            return;
        }
        try {
            setSearchingStudents(true);
            const res = await axios.get(
                getApiUrl(`students?search=${encodeURIComponent(query)}&limit=50`),
                { headers: getHeaders() }
            );
            // Exclude students already in the group
            const memberIds = new Set(currentMembers.map((m: any) => m.id));
            const students = (res.data?.students || res.data || []).filter(
                (s: any) => !memberIds.has(s.id)
            );
            setSearchedStudents(students);
        } catch (err) {
            console.error('Error searching students:', err);
        } finally {
            setSearchingStudents(false);
        }
    };

    // Load all students for a particular grade+class combination at once
    const loadByGradeClass = async (grade: string, cls: string) => {
        try {
            setSearchingStudents(true);
            // Use the grade and class fields directly
            const res = await axios.get(
                getApiUrl(`students?grade=${encodeURIComponent(grade)}&class=${encodeURIComponent(cls)}&limit=200`),
                { headers: getHeaders() }
            );
            const memberIds = new Set(currentMembers.map((m: any) => m.id));
            const students = (res.data?.students || res.data || []).filter(
                (s: any) => !memberIds.has(s.id)
            );
            setSearchedStudents(students);
            // Auto-select all found students
            setSelectedStudentIds(students.map((s: any) => s.id));
            setSuccessMsg(`Found ${students.length} students in Grade ${grade} ${cls}`);
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            console.error('Error loading by grade/class:', err);
        } finally {
            setSearchingStudents(false);
        }
    };

    const toggleStudentSelect = (studentId: string) => {
        setSelectedStudentIds(prev =>
            prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
        );
    };

    const handleAddSelectedStudents = async () => {
        if (selectedStudentIds.length === 0) return;
        try {
            await axios.post(getApiUrl(`groups/${selectedGroup.id}/students/add`),
                { studentIds: selectedStudentIds }, { headers: getHeaders() });
            setSuccessMsg(`${selectedStudentIds.length} students added to ${selectedGroup.name}`);
            // Reload members
            const res = await axios.get(getApiUrl(`groups/${selectedGroup.id}`), { headers: getHeaders() });
            setCurrentMembers(res.data?.students || []);
            setSearchedStudents([]);
            setSelectedStudentIds([]);
            fetchGroups();
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to add students');
        }
    };

    const handleRemoveStudent = async (studentId: string) => {
        try {
            await axios.post(getApiUrl(`groups/${selectedGroup.id}/students/remove`),
                { studentIds: [studentId] }, { headers: getHeaders() });
            setCurrentMembers(prev => prev.filter((s: any) => s.id !== studentId));
            fetchGroups();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove student');
        }
    };

    // ─── Filter + Display ───────────────────────────────────────────────
    const filteredGroups = groups.filter(g => {
        const matchesSearch = g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              g.grade.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              (g.teacher?.name && g.teacher.name.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCategory = activeCategoryFilter === 'ALL' || g.category === activeCategoryFilter;
        return matchesSearch && matchesCategory;
    });

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F7FAFC', minHeight: '100vh' }}>
            {/* Header */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={1} gap={2}>
                <Box>
                    <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', fontSize: { xs: '1.75rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                        Class Groups
                    </Typography>
                    <Typography variant="body1" color="text.secondary" mt={0.5}>
                        Manage academic classes, clubs, sports teams, and student memberships.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<Refresh />} onClick={fetchGroups}
                        sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568', bgcolor: 'white' }}>
                        Refresh
                    </Button>
                    {isAdmin && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}
                            sx={{ bgcolor: '#263B7C', borderRadius: '12px', px: 3, fontWeight: 700, '&:hover': { bgcolor: '#1e2e60' } }}>
                            New Group
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* Stats Ribbon */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 4, mt: 3 }}>
                {[
                    { label: 'All Groups', value: groups.length, color: '#263B7C' },
                    ...CATEGORIES.map(cat => ({
                        label: cat[0] + cat.slice(1).toLowerCase(),
                        value: groups.filter(g => g.category === cat).length,
                        color: CATEGORY_COLORS[cat].color
                    }))
                ].map((stat, i) => (
                    <Paper key={i} sx={{
                        p: 2, borderRadius: '16px', border: '1px solid #edf2f7',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        cursor: 'pointer',
                        bgcolor: activeCategoryFilter === (i === 0 ? 'ALL' : CATEGORIES[i - 1]) ? `${stat.color}10` : 'white',
                        borderColor: activeCategoryFilter === (i === 0 ? 'ALL' : CATEGORIES[i - 1]) ? stat.color : '#edf2f7',
                        transition: 'all 0.2s',
                    }}
                        onClick={() => setActiveCategoryFilter(i === 0 ? 'ALL' : CATEGORIES[i - 1])}>
                        <Typography variant="h4" fontWeight={800} color={stat.color}>{stat.value}</Typography>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">{stat.label}</Typography>
                    </Paper>
                ))}
            </Box>

            {/* Alerts */}
            {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{successMsg}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert>}

            {/* Search bar */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth size="small" placeholder="Search by name, grade, or teacher…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#A0AEC0' }} /></InputAdornment>,
                        sx: { borderRadius: '14px', bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
                    }} />
            </Box>

            {/* Groups Grid */}
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 10 }}><CircularProgress /></Box>
            ) : filteredGroups.length === 0 ? (
                <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px', bgcolor: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(10px)', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: '#EBF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GroupsIcon sx={{ fontSize: 48, color: '#4C51BF' }} />
                        </Box>
                        <Stack spacing={1}>
                            <Typography variant="h5" fontWeight="800" color="#2D3748">
                                {searchQuery || activeCategoryFilter !== 'ALL' ? 'No results found' : 'No Classes Assigned'}
                            </Typography>
                            <Typography variant="body1" color="#718096" sx={{ maxWidth: 400 }}>
                                {user?.role === 'TEACHER' 
                                    ? "You haven't been assigned to any class groups yet. Please contact your school administrator to be linked to your class."
                                    : "There are no class groups matching your current filters. Create a new group to get started."}
                            </Typography>
                        </Stack>
                        {!isAdmin && user?.role === 'TEACHER' && (
                            <Button 
                                startIcon={<Refresh />} 
                                onClick={fetchGroups}
                                sx={{ color: '#4C51BF', fontWeight: 600 }}
                            >
                                Refresh My Classes
                            </Button>
                        )}
                        {isAdmin && (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}
                                sx={{ bgcolor: '#263B7C', borderRadius: '12px', px: 4, py: 1.5, fontWeight: 700 }}>
                                Create First Group
                            </Button>
                        )}
                    </Stack>
                </Paper>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
                    {filteredGroups.map(group => (
                        <GroupCard 
                            key={group.id} 
                            group={group} 
                            isAdmin={isAdmin}
                            onEnter={() => navigate(`/dashboard/groups/${group.id}`)}
                            onRoster={() => openMembersDialog(group)}
                            onEdit={() => openEditDialog(group)}
                            onDelete={() => handleDeleteGroup(group.id, group.name)}
                        />
                    ))}
                </Box>
            )}

            {/* ─── Create Group Dialog ─── */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, color: '#263B7C' }}>Create Class Group</DialogTitle>
                <GroupForm 
                    onSubmit={handleCreateGroup} 
                    title="Create Group" 
                    formData={formData}
                    setFormData={setFormData}
                    isAdmin={isAdmin}
                    teachers={teachers}
                    onCancel={() => setCreateDialogOpen(false)}
                />
            </Dialog>

            {/* ─── Edit Group Dialog ─── */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, color: '#263B7C' }}>Edit: {selectedGroup?.name}</DialogTitle>
                <GroupForm 
                    onSubmit={handleUpdateGroup} 
                    title="Save Changes" 
                    formData={formData}
                    setFormData={setFormData}
                    isAdmin={isAdmin}
                    teachers={teachers}
                    onCancel={() => setEditDialogOpen(false)}
                />
            </Dialog>

            {/* ─── Members / Roster Management Dialog ─── */}
            <Dialog open={membersDialogOpen} onClose={() => setMembersDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontWeight: 800, color: '#263B7C', p: 3, pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={800} color="#263B7C">{selectedGroup?.name} — Student Roster</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>{currentMembers.length} enrolled students</Typography>
                        </Box>
                        <Chip label={selectedGroup?.name} sx={{ fontWeight: 800, bgcolor: '#EBF4FF', color: '#4C51BF', borderRadius: '8px' }} />
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        {/* LEFT: Search & Add Students */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={800} color="#2D3748" mb={1.5}>
                                🔍 Search & Add Students
                            </Typography>

                            {/* Quick populate by grade/class */}
                            <Paper sx={{ p: 2, borderRadius: '14px', bgcolor: '#EBF4FF', border: '1px solid #C3DAFE', mb: 2 }}>
                                <Typography variant="caption" fontWeight={800} color="#4C51BF" display="block" mb={1}>
                                    QUICK POPULATE BY CLASS
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <TextField size="small" placeholder="Grade e.g. 7"
                                        id="qs-grade"
                                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }} />
                                    <TextField size="small" placeholder="Class e.g. Green"
                                        id="qs-class"
                                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }} />
                                    <Button size="small" variant="contained"
                                        onClick={() => {
                                            const g = (document.getElementById('qs-grade') as HTMLInputElement)?.value;
                                            const c = (document.getElementById('qs-class') as HTMLInputElement)?.value;
                                            if (g || c) loadByGradeClass(g, c);
                                        }}
                                        sx={{ bgcolor: '#4C51BF', borderRadius: '10px', fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#434190' } }}>
                                        Load All
                                    </Button>
                                </Stack>
                            </Paper>

                            {/* Name search */}
                            <TextField fullWidth size="small" placeholder="Search by name, student ID…"
                                value={studentSearchQuery}
                                onChange={e => {
                                    setStudentSearchQuery(e.target.value);
                                    searchStudents(e.target.value);
                                }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#A0AEC0', fontSize: 18 }} /></InputAdornment> }}
                                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />

                            {searchingStudents && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}

                            {searchedStudents.length > 0 && (
                                <>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            {searchedStudents.length} students found
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                                onClick={() => setSelectedStudentIds(searchedStudents.map(s => s.id))}>
                                                Select All
                                            </Button>
                                            <Button size="small" sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                                onClick={() => setSelectedStudentIds([])}>
                                                Clear
                                            </Button>
                                        </Stack>
                                    </Stack>
                                    <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #edf2f7', borderRadius: '12px' }}>
                                        <List dense disablePadding>
                                            {searchedStudents.map((student) => {
                                                const isSelected = selectedStudentIds.includes(student.id);
                                                return (
                                                    <ListItem key={student.id} dense
                                                        onClick={() => toggleStudentSelect(student.id)}
                                                        sx={{ cursor: 'pointer', bgcolor: isSelected ? '#EBF4FF' : 'transparent', borderBottom: '1px solid #F7FAFC', '&:hover': { bgcolor: isSelected ? '#EBF4FF' : '#F7FAFC' } }}>
                                                        <Checkbox size="small" checked={isSelected} sx={{ p: 0.5, mr: 1, color: '#4C51BF' }} />
                                                        <ListItemAvatar sx={{ minWidth: 36 }}>
                                                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#EBF4FF', color: '#4C51BF', fontSize: '0.7rem', borderRadius: '8px' }}>
                                                                {student.firstName?.[0]}{student.lastName?.[0]}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={<Typography variant="body2" fontWeight={700}>{student.firstName} {student.lastName}</Typography>}
                                                            secondary={<Typography variant="caption" color="text.secondary">{student.studentId} · Gr {student.grade} {student.class}</Typography>}
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </Box>
                                    {selectedStudentIds.length > 0 && (
                                        <Button fullWidth variant="contained" startIcon={<PersonAddIcon />}
                                            onClick={handleAddSelectedStudents}
                                            sx={{ mt: 2, borderRadius: '12px', py: 1.2, fontWeight: 700, bgcolor: '#38A169', '&:hover': { bgcolor: '#2F855A' } }}>
                                            Add {selectedStudentIds.length} Student{selectedStudentIds.length !== 1 ? 's' : ''} to Class
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* RIGHT: Current Members */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={800} color="#2D3748" mb={1.5}>
                                👥 Current Members ({currentMembers.length})
                            </Typography>
                            {loadingMembers ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                            ) : currentMembers.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F7FAFC', borderRadius: '14px', border: '1px dashed #E2E8F0' }}>
                                    <PersonIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                                    <Typography variant="body2" color="text.secondary" fontWeight={600} mt={1}>No students enrolled yet</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #edf2f7', borderRadius: '12px' }}>
                                    <List dense disablePadding>
                                        {currentMembers.map((student: any) => (
                                            <ListItem key={student.id} dense
                                                secondaryAction={
                                                    <Tooltip title="Remove from class">
                                                        <IconButton size="small" edge="end" onClick={() => handleRemoveStudent(student.id)}
                                                            sx={{ color: '#E53E3E', '&:hover': { bgcolor: '#FFF5F5' } }}>
                                                            <PersonRemoveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                                sx={{ borderBottom: '1px solid #F7FAFC', pr: 5 }}>
                                                <ListItemAvatar sx={{ minWidth: 38 }}>
                                                    <Avatar sx={{ width: 30, height: 30, bgcolor: '#F0FFF4', color: '#38A169', fontSize: '0.7rem', borderRadius: '8px', fontWeight: 800 }}>
                                                        {student.firstName?.[0]}{student.lastName?.[0]}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={<Typography variant="body2" fontWeight={700}>{student.firstName} {student.lastName}</Typography>}
                                                    secondary={<Typography variant="caption" color="text.secondary">{student.studentId} · Gr {student.grade}</Typography>}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #edf2f7' }}>
                    <Button onClick={() => setMembersDialogOpen(false)}
                        sx={{ borderRadius: '10px', fontWeight: 700, color: '#718096' }}>Close</Button>
                    <Button variant="outlined" startIcon={<EventNoteIcon />}
                        onClick={() => { setMembersDialogOpen(false); navigate('/dashboard/attendance'); }}
                        sx={{ borderRadius: '10px', fontWeight: 700, borderColor: '#E2E8F0' }}>
                        Take Attendance
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

interface GroupCardProps {
    group: any;
    isAdmin: boolean;
    onEnter: () => void;
    onRoster: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

const GroupCard = ({ group, isAdmin, onEnter, onRoster, onEdit, onDelete }: GroupCardProps) => {
    const catConfig = CATEGORY_COLORS[group.category] || CATEGORY_COLORS['OTHER'];
    const count = group._count?.students ?? '—';
    return (
        <Paper sx={{
            borderRadius: '20px', border: '1px solid #edf2f7',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden', transition: 'all 0.3s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 30px rgba(0,0,0,0.1)' }
        }}>
            {/* Color Band */}
            <Box sx={{ height: 6, bgcolor: catConfig.color }} />
            <Box sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: catConfig.bg, color: catConfig.color, width: 48, height: 48, borderRadius: '14px', fontWeight: 900, fontSize: '1rem' }}>
                            {group.name[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={800} color="#2D3748" sx={{ lineHeight: 1.2 }}>{group.name}</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Grade {group.grade} {group.classSuffix || ''}
                            </Typography>
                        </Box>
                    </Stack>
                    <Chip label={group.category} size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', color: catConfig.color, bgcolor: catConfig.bg, borderRadius: '6px' }} />
                </Stack>

                <Stack direction="row" spacing={2} mb={2.5}>
                    <Box sx={{ textAlign: 'center', flex: 1, p: 1.5, bgcolor: '#F7FAFC', borderRadius: '12px' }}>
                        <Typography variant="h5" fontWeight={800} color="#263B7C">{count}</Typography>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">Students</Typography>
                    </Box>
                    <Box sx={{ flex: 2, p: 1.5, bgcolor: '#F7FAFC', borderRadius: '12px' }}>
                        <Typography variant="caption" fontWeight={800} color="text.secondary">TEACHER</Typography>
                        <Typography variant="body2" fontWeight={800} color="#2D3748" noWrap>{group.teacher?.name || 'Unassigned'}</Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained"
                        onClick={onEnter}
                        sx={{ flex: 2, borderRadius: '10px', fontWeight: 800, fontSize: '0.75rem', bgcolor: '#263B7C', '&:hover': { bgcolor: '#1e2e60' } }}>
                        Enter Classroom
                    </Button>
                    <Tooltip title="Manage Students">
                        <Button size="small" variant="outlined" startIcon={<PersonAddIcon />}
                            onClick={onRoster}
                            sx={{ flex: 1, borderRadius: '10px', fontWeight: 700, fontSize: '0.72rem', borderColor: '#E2E8F0', color: '#4A5568' }}>
                            Roster
                        </Button>
                    </Tooltip>
                    {isAdmin && (
                        <>
                            <Tooltip title="Edit Group">
                                <IconButton size="small" onClick={onEdit}
                                    sx={{ bgcolor: '#EBF4FF', color: '#4C51BF', borderRadius: '10px', '&:hover': { bgcolor: '#C3DAFE' } }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Group">
                                <IconButton size="small" onClick={onDelete}
                                    sx={{ bgcolor: '#FFF5F5', color: '#E53E3E', borderRadius: '10px', '&:hover': { bgcolor: '#FED7D7' } }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Stack>
            </Box>
        </Paper>
    );
};

interface GroupFormProps {
    onSubmit: (e: React.FormEvent) => void;
    title: string;
    formData: any;
    setFormData: (data: any) => void;
    isAdmin: boolean;
    teachers: any[];
    onCancel: () => void;
}

const GroupForm = ({ onSubmit, title, formData, setFormData, isAdmin, teachers, onCancel }: GroupFormProps) => (
    <form onSubmit={onSubmit}>
        <DialogContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
                <TextField fullWidth label="Group / Class Name" required
                    placeholder="e.g., Grade 7 Green, Football Team"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                <Stack direction="row" spacing={2}>
                    <TextField fullWidth label="Grade" required placeholder="e.g., 7"
                        value={formData.grade}
                        onChange={e => setFormData({ ...formData, grade: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    <TextField fullWidth label="Class Suffix" placeholder="e.g., Green, A, B"
                        value={formData.classSuffix}
                        onChange={e => setFormData({ ...formData, classSuffix: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                </Stack>
                <TextField select fullWidth label="Category"
                    value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c[0] + c.slice(1).toLowerCase()}</MenuItem>)}
                </TextField>
                {isAdmin && (
                    <TextField select fullWidth label="Assign Teacher" value={formData.teacherId}
                        onChange={e => setFormData({ ...formData, teacherId: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                        <MenuItem value="">— No Teacher Assigned —</MenuItem>
                        {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                    </TextField>
                )}
            </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={onCancel} sx={{ borderRadius: '10px', color: '#718096', fontWeight: 600 }}>Cancel</Button>
            <Button type="submit" variant="contained"
                sx={{ bgcolor: '#263B7C', borderRadius: '10px', px: 4, py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#1e2e60' } }}>
                {title}
            </Button>
        </DialogActions>
    </form>
);


export default GroupsPage;
